import { formatDate } from '@/utils/Utility.ts'
import { useSessionValues } from '@/composables/useSessionValues'
import { ref, computed } from 'vue'

const selectedDates = ref(new Set())
const activeBoostDates = ref(new Set())
const isEmptySelection = ref(false)
const selectedCommission = ref(null)
const isConfirmBoostModalOpen = ref(false)
const isChangeCommissionLevelOpen = ref(false)
const isEditingMode = ref(false)
const calendarCurrentMonth = ref(new Date())
const smartBoostPrefill = ref(null)

// Drag state management
const isDragging = ref(false)
const dragStart = ref(null)
const dragEnd = ref(null)
const hasMovedToAnotherDate = ref(false)

const generateTimestampsFromRange = (startDate, endDate) => {
  const timestamps = []
  const current = new Date(startDate)
  const end = new Date(endDate)

  while (current <= end) {
    timestamps.push(current.getTime())
    current.setDate(current.getDate() + 1)
  }

  return timestamps
}

const createDateFormatter = locale => {
  return timestamp => {
    if (!timestamp || isNaN(timestamp)) return 'Invalid Date'
    return formatDate(timestamp, locale, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    })
  }
}

const getBoostTimestamps = boost => {
  return generateTimestampsFromRange(new Date(boost.startDate + 'T00:00:00'), new Date(boost.endDate + 'T00:00:00'))
}

const createBoostRange = (boost, commissionLevel, formatDate) => {
  const timestamps = getBoostTimestamps(boost)
  const isRange = timestamps.length > 1

  return {
    timestamps,
    formattedDate: isRange
      ? `${formatDate(timestamps[0])} - ${formatDate(timestamps[timestamps.length - 1])}`
      : formatDate(timestamps[0]),
    isRange,
    baseCommissionLevel: boost.baseCommissionLevel,
    currentCommissionLevel: parseInt(commissionLevel),
    id: boost.id,
    startDate: boost.startDate,
    endDate: boost.endDate,
  }
}

export function useSmartBoost() {
  const { language } = useSessionValues()

  const setEditingMode = value => {
    isEditingMode.value = value
  }

  const switchToCreationMode = () => {
    isEditingMode.value = false
    isEmptySelection.value = true
    selectedDates.value.clear()
  }

  const switchToEditingMode = () => {
    isEditingMode.value = true
    isEmptySelection.value = false
  }

  const addDate = timestamp => {
    selectedDates.value.add(timestamp)
  }

  const removeDate = timestamp => {
    selectedDates.value.delete(timestamp)
  }

  const removeRange = timestamps => {
    const timestampsArray = Array.isArray(timestamps) ? timestamps : [timestamps]
    timestampsArray.forEach(ts => selectedDates.value.delete(ts))
  }

  const clearDates = () => selectedDates.value.clear()

  const hasDate = timestamp => selectedDates.value.has(timestamp)

  const toggleDate = timestamp => {
    hasDate(timestamp) ? removeDate(timestamp) : addDate(timestamp)
  }

  const setRange = (startTimestamp, endTimestamp) => {
    const timestamps = generateTimestampsFromRange(
      Math.min(startTimestamp, endTimestamp),
      Math.max(startTimestamp, endTimestamp)
    )
    timestamps.forEach(ts => selectedDates.value.add(ts))
  }

  const formatDateRanges = (timestamps, locale) => {
    if (!timestamps?.length) return []

    const sorted = [...timestamps].sort((a, b) => a - b)
    const ranges = []
    const formatDate = createDateFormatter(locale)

    let rangeStart = sorted[0]
    let rangeTimestamps = [rangeStart]

    for (let i = 1; i <= sorted.length; i++) {
      const isConsecutive = i < sorted.length && isNextDay(rangeTimestamps[rangeTimestamps.length - 1], sorted[i])

      if (isConsecutive) {
        rangeTimestamps.push(sorted[i])
      } else {
        const isRange = rangeTimestamps.length > 1
        ranges.push({
          timestamps: rangeTimestamps,
          formattedDate: isRange
            ? `${formatDate(rangeStart)} - ${formatDate(rangeTimestamps[rangeTimestamps.length - 1])}`
            : formatDate(rangeStart),
          isRange,
        })

        if (i < sorted.length) {
          rangeStart = sorted[i]
          rangeTimestamps = [sorted[i]]
        }
      }
    }

    return ranges
  }

  const isNextDay = (timestamp1, timestamp2) => {
    const date1 = new Date(timestamp1)
    const date2 = new Date(timestamp2)

    const nextDay = new Date(date1)
    nextDay.setDate(nextDay.getDate() + 1)

    return (
      nextDay.getFullYear() === date2.getFullYear() &&
      nextDay.getMonth() === date2.getMonth() &&
      nextDay.getDate() === date2.getDate()
    )
  }

  const formatDateRangesByBoost = (timestamps, locale, activeBoosts) => {
    if (!timestamps?.length || !activeBoosts) return []

    const timestampsSet = new Set(timestamps)
    const ranges = []
    const formatDate = createDateFormatter(locale)

    for (const [commissionLevel, boosts] of Object.entries(activeBoosts)) {
      for (const boost of boosts) {
        const boostTimestamps = getBoostTimestamps(boost)
        const allSelected = boostTimestamps.every(ts => timestampsSet.has(ts))

        if (allSelected) {
          ranges.push(createBoostRange(boost, commissionLevel, formatDate))
        }
      }
    }

    return ranges.sort((a, b) => a.timestamps[0] - b.timestamps[0])
  }

  const setActiveBoostDates = timestamps => {
    activeBoostDates.value = new Set(timestamps)
  }

  const clearActiveBoostDates = () => activeBoostDates.value.clear()

  const hasActiveBoostDate = timestamp => activeBoostDates.value.has(timestamp)

  const loadExistingActiveBoostsForEditing = timestamps => {
    activeBoostDates.value = new Set(timestamps)
    selectedDates.value = new Set(timestamps)
  }

  const openEmptySelection = () => {
    isEmptySelection.value = true
    selectedDates.value.clear()
  }

  const cancelEmptySelection = () => {
    isEmptySelection.value = false
    selectedDates.value = new Set(activeBoostDates.value)
    if (activeBoostDates.value.size > 0) {
      isEditingMode.value = true
    }
  }

  const selectCommission = newCommission => {
    selectedCommission.value = newCommission
  }

  const toggleConfirmBoostModal = status => {
    isConfirmBoostModalOpen.value = status === 'open'
  }

  const openChangeCommissionLevelModal = () => {
    isChangeCommissionLevelOpen.value = true
  }

  const closeChangeCommissionLevelModal = () => {
    isChangeCommissionLevelOpen.value = false
  }

  // ✅ ADD: Drag state management functions
  const setDragging = value => {
    isDragging.value = value
  }

  const setDragStart = value => {
    dragStart.value = value
  }

  const setDragEnd = value => {
    dragEnd.value = value
  }

  const setHasMovedToAnotherDate = value => {
    hasMovedToAnotherDate.value = value
  }

  const resetDragState = () => {
    isDragging.value = false
    dragStart.value = null
    dragEnd.value = null
    hasMovedToAnotherDate.value = false
  }

  const getSelectedDatesArray = computed(() => Array.from(selectedDates.value).sort((a, b) => a - b))

  const getSelectedDatesFormatted = computed(() => formatDateRanges(Array.from(selectedDates.value), language.value))

  const resetCalendarMonth = () => {
    calendarCurrentMonth.value = new Date()
  }

  const setSmartBoostPrefill = prefill => {
    smartBoostPrefill.value = prefill
  }

  const consumeSmartBoostPrefill = () => {
    const prefill = smartBoostPrefill.value
    smartBoostPrefill.value = null
    return prefill
  }

  const countTotalActiveBoostRanges = activeBoostsData => {
    if (!activeBoostsData || Object.keys(activeBoostsData).length === 0) {
      return 0
    }

    let total = 0
    for (const boosts of Object.values(activeBoostsData)) {
      total += boosts.length
    }
    return total
  }

  return {
    isEditingMode,
    setEditingMode,
    switchToCreationMode,
    switchToEditingMode,
    addDate,
    removeDate,
    removeRange,
    clearDates,
    hasDate,
    toggleDate,
    setRange,
    formatDateRanges,
    formatDateRangesByBoost,
    setActiveBoostDates,
    clearActiveBoostDates,
    hasActiveBoostDate,
    loadExistingActiveBoostsForEditing,
    openEmptySelection,
    cancelEmptySelection,
    selectCommission,
    toggleConfirmBoostModal,
    openChangeCommissionLevelModal,
    closeChangeCommissionLevelModal,
    getBoostTimestamps,
    resetCalendarMonth,
    setSmartBoostPrefill,
    consumeSmartBoostPrefill,
    countTotalActiveBoostRanges,
    isConfirmBoostModalOpen,
    isChangeCommissionLevelOpen,
    isEmptySelection,
    calendarCurrentMonth,
    selectedCommission,
    selectedDates,
    activeBoostDates,
    getSelectedDatesArray,
    getSelectedDatesFormatted,
    // Drag state management
    isDragging,
    dragStart,
    dragEnd,
    hasMovedToAnotherDate,
    setDragging,
    setDragStart,
    setDragEnd,
    setHasMovedToAnotherDate,
    resetDragState,
  }
}
